import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, Plus, Lock, ShieldCheck, MessageSquare, ArrowLeft, X,
} from 'lucide-react';
import { COLORS } from '../data/mockData';
import {
  fetchGroupChatRooms, createGroupChatRoom, fetchGroupChatConsent,
} from '../services/api';

const CAT_LABELS = {
  sleep: 'Sleep', headspace: 'Headspace', interaction: 'Interaction',
  exercise: 'Exercise', limit_exposure: 'Limit Exposure', diet: 'Diet',
  environment: 'Environment', discovery: 'Discovery',
  genetics_biomarkers: 'Genetics & Biomarkers',
  general_longevity: 'General Longevity',
};

export default function GroupChatLobbyPage() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [allowedInterests, setAllowedInterests] = useState([]);

  const load = () => {
    setLoading(true);
    fetchGroupChatRooms()
      .then(setRooms)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    fetchGroupChatConsent().then((c) => setAllowedInterests(c.allowed_interests || []));
  }, []);

  return (
    <div data-testid="group-chat-lobby" style={{ maxWidth: 880, margin: '0 auto', padding: '16px 24px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button onClick={() => navigate('/dashboard/chat')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ArrowLeft size={22} color={COLORS.textPrimary} />
        </button>
        <Users size={26} color={COLORS.primary} />
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Group Chats</h1>
        <button
          data-testid="create-room-btn"
          onClick={() => setShowCreate(true)}
          style={{ marginLeft: 'auto', padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', backgroundColor: COLORS.primary, color: '#FFF', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={16} /> New Room
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14, backgroundColor: '#FFF8E1', border: '1px solid #F0D77A', borderRadius: 14, marginBottom: 16 }}>
        <ShieldCheck size={20} color="#B58A00" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, color: '#7A5C00', lineHeight: 1.5 }}>
          Every room is moderated in real-time by an AI moderator. Messages may be redacted or removed; transcripts are stored and analysed for sentiment and keyword indexing. By joining a room you accept the privacy notice, key terms, and subject-matter constraints.
        </div>
      </div>

      {loading && <p style={{ color: COLORS.textSecondary }}>Loading rooms…</p>}

      {!loading && rooms.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 16px', backgroundColor: COLORS.surface, borderRadius: 18 }}>
          <MessageSquare size={42} color={COLORS.border} />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginTop: 10, fontFamily: 'Outfit, sans-serif' }}>No group chats yet</h2>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, maxWidth: 380, margin: '6px auto 18px' }}>
            Be the first to start a SHIELDED-topic room. Invite peers, share experiences, and earn community credit for the most up-voted contributions.
          </p>
          <button onClick={() => setShowCreate(true)} style={{ padding: '12px 22px', borderRadius: 9999, border: 'none', cursor: 'pointer', backgroundColor: COLORS.primary, color: '#FFF', fontSize: 14, fontWeight: 700 }}>
            Create First Room
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {rooms.map((r) => (
          <Link
            key={r.room_id}
            to={`/dashboard/chat/groups/${r.room_id}`}
            data-testid={`room-card-${r.room_id}`}
            style={{ textDecoration: 'none', backgroundColor: COLORS.surface, borderRadius: 14, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              {r.visibility === 'invite_only' && <Lock size={12} color={COLORS.textSecondary} />}
              <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 1 }}>{CAT_LABELS[r.topic] || r.topic}</span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{r.name}</div>
            {r.description && <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 6, lineHeight: 1.45 }}>{r.description}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 10, fontSize: 12, color: COLORS.textSecondary }}>
              <span><Users size={11} style={{ verticalAlign: 'middle' }} /> {r.member_count} member{r.member_count === 1 ? '' : 's'}</span>
              {r.is_member && <span style={{ color: COLORS.success, fontWeight: 700 }}>● Joined</span>}
            </div>
          </Link>
        ))}
      </div>

      {showCreate && (
        <CreateRoomModal
          allowedInterests={allowedInterests}
          onClose={() => setShowCreate(false)}
          onCreated={(r) => { setShowCreate(false); navigate(`/dashboard/chat/groups/${r.room_id}`); }}
        />
      )}
    </div>
  );
}


function CreateRoomModal({ allowedInterests, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [topic, setTopic] = useState('general_longevity');
  const [visibility, setVisibility] = useState('invite_only');
  const [interests, setInterests] = useState(['general_longevity']);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const toggleInterest = (k) => {
    setInterests((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const submit = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr(null);
    try {
      const room = await createGroupChatRoom({
        name: name.trim(),
        description: description.trim(),
        topic,
        allowed_interests: interests.length ? interests : [topic],
        visibility,
      });
      onCreated(room);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to create room');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="create-room-modal" style={{ backgroundColor: COLORS.background, borderRadius: 18, width: '100%', maxWidth: 540, padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>New Group Chat</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color={COLORS.textPrimary} /></button>
        </div>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary }}>Room name</span>
          <input data-testid="room-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Sleep Optimizers" style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginTop: 4, fontSize: 14 }} />
        </label>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary }}>Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={600} placeholder="What's this room about?" style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginTop: 4, fontSize: 14, minHeight: 70, resize: 'vertical' }} />
        </label>

        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary }}>Primary topic</span>
          <select data-testid="room-topic-select" value={topic} onChange={(e) => setTopic(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginTop: 4, fontSize: 14, backgroundColor: COLORS.surface }}>
            {allowedInterests.map((k) => <option key={k} value={k}>{CAT_LABELS[k] || k}</option>)}
          </select>
        </label>

        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary }}>Allowed interest tags (filter for moderator)</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {allowedInterests.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => toggleInterest(k)}
                style={{
                  padding: '6px 10px', borderRadius: 9999, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600,
                  backgroundColor: interests.includes(k) ? COLORS.primary : `${COLORS.primary}15`,
                  color: interests.includes(k) ? '#FFF' : COLORS.primary,
                }}
              >{CAT_LABELS[k] || k}</button>
            ))}
          </div>
        </div>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary }}>Visibility</span>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${COLORS.border}`, marginTop: 4, fontSize: 14, backgroundColor: COLORS.surface }}>
            <option value="invite_only">Invite-only (you invite by email)</option>
            <option value="members">Open to all members</option>
          </select>
        </label>

        {err && <div style={{ color: COLORS.error, fontSize: 13, marginBottom: 8 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>Cancel</button>
          <button data-testid="submit-create-room" onClick={submit} disabled={saving} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Creating…' : 'Create Room'}
          </button>
        </div>
      </div>
    </div>
  );
}
